'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useAuth } from './AuthContext';

/**
 * @typedef {Object} NotificationContextValue
 * @property {'default'|'granted'|'denied'} permission - Estado del permiso de notificaciones
 * @property {boolean} isSubscribed - Si el usuario está suscrito a push notifications
 * @property {boolean} isLoading - Si hay una operación en curso
 * @property {string|null} error - Mensaje de error si algo falló
 * @property {Function} requestPermission - Solicita permiso para notificaciones
 * @property {Function} subscribeToPush - Suscribe a notificaciones push
 * @property {Function} getScheduledNotifications - Obtiene notificaciones del día
 * @property {Function} scheduleToday - Programa notificaciones del día actual
 * @property {Function} sendMessage - Envía mensaje al service worker
 * @property {Function} clearError - Limpia el error actual
 */

const NotificationContext = createContext(null);

/**
 * Provider del sistema de notificaciones
 * Maneja permisos, suscripciones push y comunicación con service worker
 * 
 * IMPORTANTE: Las notificaciones vienen PRE-TRADUCIDAS del servidor
 * según el idioma del usuario (serverNotificationTranslator)
 */
export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const locale = useLocale();
  const tNotificationsSystem = useTranslations('notifications.system');
  const [permission, setPermission] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastAuthPushUserIdRef = useRef(null);

  const getInstallationId = useCallback(() => {
    if (typeof window === 'undefined') return null;

    const storageKey = 'aifit-installation-id';
    const existing = window.localStorage.getItem(storageKey);
    if (existing) return existing;

    const generated =
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    window.localStorage.setItem(storageKey, generated);
    return generated;
  }, []);

  function isNotificationDue(scheduledFor) {
    if (!scheduledFor) return false;
    const now = Date.now();
    const scheduled = new Date(scheduledFor).getTime();
    if (!Number.isFinite(scheduled)) return false;
    return Math.abs(now - scheduled) <= 15 * 60 * 1000;
  }

  /**
   * Inicializar estado de notificaciones (sin registrar service worker)
   */
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    } else {
      setPermission('denied');
    }
    // Registrar Service Worker si está disponible
    async function initServiceWorker() {
      if (!('serviceWorker' in navigator)) {
        console.warn('[NotificationContext] Service Worker no soportado');
        return;
      }

      try {
        const reg = await navigator.serviceWorker.register('/service-worker.js');
        await navigator.serviceWorker.ready;
        setRegistration(reg);

        // Soft update UX: PwaUpdateBanner prompts reload. Do not force reload here.

        // When navigate() is unavailable (common on Android PWA), SW asks the page to route.
        navigator.serviceWorker.addEventListener('message', (event) => {
          const payload = event?.data;
          if (payload?.type !== 'NOTIFICATION_NAVIGATE') return;
          const target = String(payload.url || '').trim();
          if (!target) return;
          try {
            const absolute = new URL(target, window.location.origin);
            if (absolute.origin !== window.location.origin) return;
            // Never honor a stale navigate-to-register from an old SW message.
            if (/\/register\/?$/.test(absolute.pathname)) {
              absolute.pathname = `/${locale}/login`;
              absolute.search = `next=/${locale}/dashboard`;
            }
            window.location.assign(absolute.href);
          } catch {
            // ignore malformed urls
          }
        });

        const existingSubscription = await reg.pushManager.getSubscription();
        setIsSubscribed(Boolean(existingSubscription));
      } catch (swError) {
        console.error('[NotificationContext] Error registrando Service Worker:', swError);
      }
    }

    initServiceWorker();
  }, [locale]);

  /**
   * Suscribirse a notificaciones push
   * Guarda la suscripción en backend para envíos futuros
   * @returns {Promise<boolean>} true si se suscribió correctamente
   */
  const subscribeToPush = useCallback(async () => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!registration) {
      console.log('[NotificationContext] ⚠️ Service Worker no registrado aún');
      return false;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar si ya existe suscripción
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Crear nueva suscripción
        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        
        if (!vapidPublicKey) {
          console.warn('[NotificationContext] VAPID_PUBLIC_KEY no configurada; se omite suscripcion push.');
          return false;
        }
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        
        console.log('[NotificationContext] ✅ Nueva suscripción push creada');
      } else {
        console.log('[NotificationContext] ℹ️ Usando suscripción existente');
      }

      if (user?.id) {
        // Guardar suscripción en backend cuando hay usuario autenticado
        const timeZone =
          typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : 'America/Bogota';
        const response = await fetch('/api/notifications/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            subscription: subscription.toJSON(),
            installationId: getInstallationId(),
            locale,
            timeZone,
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } else {
        const installationId = getInstallationId();
        const guestResponse = await fetch('/api/notifications/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            installationId,
            locale,
            subscription: subscription.toJSON()
          })
        });

        if (!guestResponse.ok) {
          throw new Error(`HTTP ${guestResponse.status}: ${guestResponse.statusText}`);
        }
      }

      setIsSubscribed(true);
      console.log('[NotificationContext] ✅ Suscripción guardada en servidor');
      return true;
      
    } catch (error) {
      console.error('[NotificationContext] ❌ Error suscribiendo a push:', error);
      setError(tNotificationsSystem('pushActivationFailed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [registration, tNotificationsSystem, user, getInstallationId, locale]);

  /**
   * Solicitar permiso para notificaciones
   * @returns {Promise<boolean>} true si se concedió, false si se denegó
   */
  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!('Notification' in window)) {
      console.error('[NotificationContext] ❌ Notificaciones no soportadas');
      setError(tNotificationsSystem('browserNotSupported'));
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted') {
        console.log('[NotificationContext] ✅ Permiso concedido');
        await subscribeToPush();
        return true;
      } else {
        console.log('[NotificationContext] ⚠️ Permiso denegado por el usuario');
        setError(tNotificationsSystem('permissionDenied'));
        return false;
      }
    } catch (error) {
      console.error('[NotificationContext] ❌ Error solicitando permiso:', error);
      setError(tNotificationsSystem('permissionRequestFailed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscribeToPush, tNotificationsSystem]);

  /**
   * Enviar mensaje al service worker
   * @param {Object} message - Mensaje a enviar
   */
  const sendMessage = useCallback((message) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!registration || !registration.active) {
      console.warn('[NotificationContext] ⚠️ Service Worker no activo, no se puede enviar mensaje');
      return;
    }

    console.log('[NotificationContext] 📤 Enviando mensaje al SW:', message.type);
    registration.active.postMessage(message);
  }, [registration]);

  /**
   * Obtener notificaciones programadas del día
   * Las notificaciones vienen PRE-TRADUCIDAS del servidor según idioma del usuario
   * @returns {Promise<Array>} Lista de notificaciones
   */
  const getScheduledNotifications = useCallback(async () => {
    if (typeof window === 'undefined') {
      return [];
    }

    if (!user) {
      console.log('[NotificationContext] ℹ️ Modo invitado activo: no se consultan notificaciones personalizadas');
      return [];
    }

    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota';
      const response = await fetch(
        `/api/notifications/schedule?userId=${user.id}&timeZone=${encodeURIComponent(timeZone)}&locale=${encodeURIComponent(locale)}`,
        { method: 'GET' }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      const notifications = data.notifications || [];
      
      console.log(`[NotificationContext] ✅ ${notifications.length} notificaciones obtenidas`);
      return notifications;
      
    } catch (error) {
      console.error('[NotificationContext] ❌ Error obteniendo notificaciones:', error);
      setError(tNotificationsSystem('scheduledFetchFailed'));
      return [];
    }
  }, [locale, tNotificationsSystem, user]);

  /**
   * Programar notificaciones del día (generar nuevas notificaciones)
   * Las notificaciones se generan en el servidor con translation_keys
   * y se traducen automáticamente según el idioma del usuario
   * @returns {Promise<Object|null>} Respuesta del servidor
   */
  const scheduleToday = useCallback(async () => {
    if (typeof window === 'undefined') {
      return null;
    }

    if (!user) {
      console.log('[NotificationContext] ⚠️ No hay usuario autenticado');
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[NotificationContext] 📅 Programando notificaciones del día...');
      
      const response = await fetch('/api/notifications/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota',
          locale
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log(`[NotificationContext] ✅ ${data.count} notificaciones programadas exitosamente`);
        console.log('[NotificationContext] 📊 Desglose:', data.breakdown);
        
        // Notificar al service worker que hay nuevas notificaciones
        sendMessage({
          type: 'NOTIFICATIONS_UPDATED',
          count: data.count
        });
      }
      
      return data;
      
    } catch (error) {
      console.error('[NotificationContext] ❌ Error programando notificaciones:', error);
      setError(tNotificationsSystem('scheduleFailed'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [locale, sendMessage, tNotificationsSystem, user]);

  const announceNotification = useCallback((notification, { speak = false } = {}) => {
    if (typeof window === 'undefined' || !notification) return;

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator && notification.vibration) {
      navigator.vibrate(Array.isArray(notification.vibrate) ? notification.vibrate : [200, 100, 200]);
    }

    // Auto-check must stay quiet; speech is reserved for explicit user-facing cues.
    if (!speak) return;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.value = 880;
        gainNode.gain.value = 0.05;

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.12);
        oscillator.onended = () => {
          ctx.close().catch(() => {});
        };
      }
    } catch {
      // Ignore audio blocks.
    }

    if ('speechSynthesis' in window && document.visibilityState === 'visible' && notification.title) {
      const utterance = new SpeechSynthesisUtterance(
        [notification.title, notification.message].filter(Boolean).join('. ')
      );
      utterance.lang = locale;
      utterance.rate = 0.95;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
  }, [locale]);

  /**
   * Escuchar mensajes del service worker
   * El SW puede solicitar notificaciones o marcar notificaciones como enviadas
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !registration || !('serviceWorker' in navigator)) return;

    const handleMessage = async (event) => {
      const { type, data } = event.data || {};

      console.log('[NotificationContext] 📨 Mensaje recibido del SW:', type);

      switch (type) {
        case 'GET_SCHEDULED_NOTIFICATIONS':
          // Service worker solicita notificaciones programadas
          console.log('[NotificationContext] SW solicita notificaciones programadas');
          const notifications = await getScheduledNotifications();
          sendMessage({
            type: 'SCHEDULED_NOTIFICATIONS_DATA',
            notifications
          });
          break;

        case 'MARK_NOTIFICATION_SENT':
          // Marcar notificación como enviada en backend
          if (!user) {
            return;
          }
          try {
            const response = await fetch('/api/notifications/mark-delivered', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: data?.notificationId ?? data?.id })
            });
            
            if (response.ok) {
              console.log(`[NotificationContext] ✅ Notificación ${data?.notificationId} marcada como enviada`);
            }
          } catch (error) {
            console.error('[NotificationContext] ❌ Error marcando notificación:', error);
          }
          break;

        default:
          console.log('[NotificationContext] ⚠️ Tipo de mensaje desconocido:', type);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [registration, getScheduledNotifications, sendMessage, user]);

  /**
   * Auto-suscribir si el usuario ya dio permiso.
   * También re-sincroniza una vez al pasar de invitado → sesión iniciada
   * (antes isSubscribed=true bloqueaba el upgrade a user_push_subscriptions).
   */
  useEffect(() => {
    if (permission !== 'granted' || !registration || isLoading) return;

    if (!user?.id) {
      lastAuthPushUserIdRef.current = null;
    }

    if (!isSubscribed) {
      console.log('[NotificationContext] ℹ️ Permiso ya concedido, auto-suscribiendo...');
      subscribeToPush();
      return;
    }

    if (user?.id && lastAuthPushUserIdRef.current !== user.id) {
      lastAuthPushUserIdRef.current = user.id;
      console.log('[NotificationContext] ℹ️ Sesión activa: sincronizando push autenticado...');
      subscribeToPush();
    }
  }, [permission, isSubscribed, registration, isLoading, subscribeToPush, user?.id]);

  // Verificador local: muestra como máximo 1 alerta por ciclo; limpia backlog viejo en silencio.
  useEffect(() => {
    if (typeof window === 'undefined' || !registration || permission !== 'granted') return;

    let cancelled = false;

    const markDelivered = async (ids) => {
      if (!user || !ids?.length) return;
      await fetch('/api/notifications/mark-delivered', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
    };

    const runDueCheck = async () => {
      const notifications = await getScheduledNotifications();
      if (cancelled || !Array.isArray(notifications)) return;

      const now = Date.now();
      const staleIds = [];
      const dueNow = [];

      for (const notif of notifications) {
        if (notif?.sent || !notif?.scheduled_for) continue;
        const scheduled = new Date(notif.scheduled_for).getTime();
        if (!Number.isFinite(scheduled)) continue;

        const delta = now - scheduled; // >0 overdue, <0 upcoming
        const isMorningBrief = String(notif.translation_key || '').includes('morning_brief');
        // Align with lib/notification-delivery-policy.js (15 min normal / 60 min high).
        const catchUpMs =
          notif.priority === 'high' || isMorningBrief ? 60 * 60 * 1000 : 15 * 60 * 1000;

        if (delta > catchUpMs) {
          // Older than catch-up window: clear without spamming.
          staleIds.push(notif.id);
          continue;
        }
        if (delta >= -2 * 60 * 1000) {
          // Due soon (≤2 min early) or overdue within catch-up.
          dueNow.push(notif);
        }
      }

      if (staleIds.length > 0) {
        await markDelivered(staleIds);
      }

      if (dueNow.length === 0) return;

      dueNow.sort((a, b) => {
        const rank = (n) => {
          if (String(n.translation_key || '').includes('morning_brief')) return 0;
          if (n.priority === 'high') return 1;
          return 2;
        };
        return rank(a) - rank(b);
      });

      // One OS notification max per minute to avoid reload storms.
      const next = dueNow[0];
      const restIds = dueNow.slice(1).map((n) => n.id).filter(Boolean);

      // Mark first before display so a failed SW call does not retrigger forever.
      await markDelivered([next.id, ...restIds].filter(Boolean));

      announceNotification(next, { speak: false });
      sendMessage({
        type: 'DISPLAY_NOTIFICATION',
        notification: {
          ...next,
          url: next.deeplink || next.url || `/${locale}/dashboard`,
          deeplink: next.deeplink || next.url || `/${locale}/dashboard`,
          tag: next.id || next.tag,
        },
      });
    };

    runDueCheck();
    const intervalId = setInterval(runDueCheck, 60000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    user,
    registration,
    permission,
    locale,
    announceNotification,
    getScheduledNotifications,
    sendMessage,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user || permission !== 'granted') return;

    const cacheKey = `aifit-notification-schedule-${new Date().toISOString().split('T')[0]}`;
    if (window.localStorage.getItem(cacheKey) === 'done') return;

    scheduleToday().then((result) => {
      if (result?.success) {
        window.localStorage.setItem(cacheKey, 'done');
      }
    });
  }, [permission, scheduleToday, user]);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    // Estado
    permission,
    isSubscribed,
    isLoading,
    error,
    
    // Acciones
    requestPermission,
    subscribeToPush,
    getScheduledNotifications,
    scheduleToday,
    sendMessage,
    clearError
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook para usar el sistema de notificaciones
 * @returns {NotificationContextValue}
 * @throws {Error} Si se usa fuera del NotificationProvider
 */
export function useNotificationSystem() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationSystem debe usarse dentro de NotificationProvider');
  }
  return context;
}

/**
 * Convertir clave VAPID base64 a Uint8Array
 * Necesaria para suscribirse a push notifications
 * @param {string} base64String - Clave VAPID en formato base64 URL-safe
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const atobFn =
    (typeof window !== 'undefined' && typeof window.atob === 'function' && window.atob.bind(window)) ||
    (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function' && globalThis.atob.bind(globalThis));

  if (!atobFn) {
    throw new Error('atob no disponible en este entorno');
  }

  const rawData = atobFn(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}



